/**
 * 003 — /cashflow: thin route. Tab, period, account, date basis and Ist
 * quarter are URL state (validateSearch); the page lives in
 * components/bank/CashflowTabs.tsx.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { CashflowTabs, type CashflowSearch } from '../components/bank/CashflowTabs';
import { useSession } from '../hooks/useTenantContext';
import { quarterOf } from '../lib/bank-api';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const today = () => new Date().toISOString().slice(0, 10);

const searchSchema = z.object({
  tab: z.enum(['uebersicht', 'buchungen', 'lastschriften', 'ist', 'abdeckung', 'partner']).optional().catch(undefined),
  from: z.string().regex(DAY).optional().catch(undefined),
  to: z.string().regex(DAY).optional().catch(undefined),
  account: z.string().optional().catch(undefined),
  basis: z.enum(['invoiceDate', 'paymentDate']).optional().catch(undefined),
  quarter: z.string().regex(/^\d{4}-Q[1-4]$/).optional().catch(undefined),
});

export const Route = createFileRoute('/cashflow')({
  component: CashflowPage,
  validateSearch: searchSchema,
});

function CashflowPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <CashflowInner />
      </AppShell>
    </ProtectedRoute>
  );
}

function CashflowInner() {
  const { data: session } = useSession();
  const raw = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const to = raw.to ?? today();
  const from = raw.from ?? `${to.slice(0, 4)}-01-01`;
  const search: CashflowSearch = {
    tab: raw.tab ?? 'uebersicht',
    from,
    to,
    account: raw.account,
    basis: raw.basis ?? 'invoiceDate',
    quarter: raw.quarter ?? quarterOf(to),
  };
  if (session?.activeRole === 'admin')
    return <p>Bankdaten sind ausschließlich für Geschäftsführung und Steuerberatung zugänglich.</p>;
  const allowed = session?.activeRole === 'geschäftsführer' || session?.activeRole === 'steuerberater';
  return (
    <CashflowTabs
      search={search}
      onSearch={(patch) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })}
      tenantId={allowed ? (session?.activeTenantId ?? undefined) : undefined}
      role={session?.activeRole}
    />
  );
}
